import React, {CSSProperties, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faTrash} from "@fortawesome/pro-regular-svg-icons";
import {ClavaContext} from "../../../config/contexts";
import {Image as UserImage, MediaCrop} from "../../../client/api";
import ClavaImage from "../ClavaImage";
import ClavaButton from "../ClavaButton";
import {CloudflareImage} from "../../../config/types";
import {translate} from "../../../config/translator";
import {uploadCloudflare} from "../../../config/mediaUtils";
import client from "../../../client";

const FileUploaderPreview: React.FC<{
    file?: File;
    img?: UserImage;
    crop: MediaCrop | undefined;
    toggleCropper: () => void;
    callback: (cloudflareImage?: CloudflareImage, img?: UserImage) => void;
}> = ({file, crop, toggleCropper, callback, img}) => {
    const {l, user} = useContext(ClavaContext);
    const [size, setSize] = useState<{ w: number; h: number }>();
    const src = useMemo(
        () => (file ? URL.createObjectURL(file) : undefined),
        [file]
    );
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploaded, setUploaded] = useState<CloudflareImage>();

    const [error, setError] = useState<string>();

    useEffect(() => {
        const image = new Image();
        image.onload = () => {
            setSize({w: image.width, h: image.height});
        };
        image.src = src ?? "";
    }, [src]);

    const previewFile = useMemo<UserImage | undefined>(() => {
        if (img) {
            return img;
        }
        if (size && crop) {
            return {
                uploadedById: user.id,
                url: src,
                uploadDate: "",
                id: "asf",
                aspectRatio: size.w / size.h,
                ...crop
            } as UserImage;
        }
        return undefined;
    }, [crop, img, size, src, user.id]);

    const onStartUpload = useCallback(
        () => {
            if (img) {
                callback(undefined, img);
            } else if (!img && file && size && src && crop) {
                setIsUploading(true);
                toggleCropper();
                setProgress(0);
                uploadCloudflare(
                    {
                        file,
                        as: size.w / size.h,
                        url: src,
                        filename: file.name
                    },
                    crop,
                    (sent, total) => {
                        setProgress((sent / total) * 100);
                    }
                )
                    .then(
                        (res) => {
                            setUploaded(res);
                            callback(res);
                        },
                        (err) => {
                            if (typeof err === "string" && err === "error")
                                setError(translate("uploadFailed", l));
                            else if (
                                typeof err === "string" &&
                                err.startsWith("error_length")
                            ) {
                                setError(translate("imageTooBig", l));
                            } else if (typeof err === "object" && "errors" in err) {
                                // @ts-ignore
                                setError(err.errors.map((e) => e.message).join(", "));
                            } else {
                                setError(translate("uploadFailed", l));
                            }
                        }
                    )
                    .finally(() => {
                        setIsUploading(false);
                    });
            }
        },
        [img, file, size, src, crop, toggleCropper, callback, l]
    );

    const progressStyle = useMemo<CSSProperties>(() => ({
        width: `${progress}%`
    }), [progress]);

    const onDelete = useCallback(() => {
        setUploaded(undefined);
        toggleCropper();
        if (uploaded) {
            if (uploaded.path.includes("video.mdp")) {
                client()
                    .deleteVideoCloudflare(uploaded.id)
                    .then(() => {
                        toggleCropper();
                    });
            } else {
                client()
                    .deleteImageCloudflare(uploaded.id)
                    .then(() => {
                        toggleCropper();
                    });
            }
        }
    }, [uploaded, toggleCropper]);

    const imageStyle = useMemo(() => {
        if (!crop || !crop.cropAspectRatio) {
            return {width: "100%"};
        }
        if (crop.cropAspectRatio > 3) {
            return {width: "100%"};
        }
        return {
            width: `${crop.cropAspectRatio * 25}%`
        };
    }, [crop]);

    const onSave = useCallback(() => {
        callback(uploaded);
    }, [uploaded, callback]);

    if (!previewFile) return null;
    return (
        <div className="flex flex-row items-center justify-start">
            {!!error && (
                <div className="flex flex-col items-center justify-start">
                    <span className="text-red font-semibold mb-4 block">{error}</span>
                </div>
            )}
            <div className="flex-1 relative mr-4 flex flex-row items-center justify-center">
                <div
                    style={imageStyle}
                    className="pattern-rectangles pattern-pattern-gray pattern-bg-white
  pattern-size-4 pattern-opacity-100"
                >
                    <ClavaImage width="100%" image={previewFile}/>
                </div>
            </div>
            <div className="flex-1">
                {isUploading ? (
                    <div
                        className="w-full relative h-6 rounded-lg bg-light-gray dark:bg-light-gray-dark flex flex-col items-center justify-center">
                        <div
                            className="absolute top-0 left-0 h-6 rounded-lg bg-primary flex flex-row items-center justify-center overflow-hidden"
                            style={progressStyle}
                        />
                        <span className="text-black dark:text-white absolute">
              {`${progress.toFixed(1)} %`}
            </span>
                    </div>
                ) : uploaded ? (
                    <div className="flex flex-row items-center justify-start">
                        <span className="italic mr-4">{uploaded.id}</span>
                        <ClavaButton onClick={onDelete} danger>
                            <FontAwesomeIcon icon={faTrash}/>
                        </ClavaButton>
                        <ClavaButton onClick={onSave}>
                            <FontAwesomeIcon icon={faCheck} color="#fff"/>
                        </ClavaButton>
                    </div>
                ) : !error ? (
                    <ClavaButton onClick={onStartUpload}>
                        {translate("save", l)}
                    </ClavaButton>
                ) : null}
            </div>
        </div>
    );
};

export default FileUploaderPreview;
